import {
  Component,
  computed,
  effect,
  inject,
  TemplateRef,
  ViewChild,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { EquipmentStore } from '@features/catalog/store';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import {
  DataTableComponent,
  ColumnDef,
  TableAction,
} from '@shared/components/data-table/data-table';
import { SolarInverter } from '@features/catalog/models';
import { CreateInverterDialog } from '@shared/components/create-inverter-dialog/create-inverter-dialog';

@Component({
  selector: 'app-inverters-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    DataTableComponent,
    CreateInverterDialog,
  ],
  templateUrl: './inverters-list.html',
  styleUrl: './inverters-list.css',
})
export class InvertersListComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  readonly queryParams = toSignal(this.route.queryParamMap, { initialValue: this.route.snapshot.queryParamMap });
  readonly linkedEquipment = computed(() => this.queryParams().get('equipment'));
  readonly visibleEquipment = computed(() => {
    const id = this.linkedEquipment();
    const items = this.equipmentStore.allInverters();
    return id ? items.filter(item => item.id === id) : items;
  });
  private readonly equipmentStore = inject(EquipmentStore);
  readonly currentOrgStore = inject(CurrentOrganizationStore);

  @ViewChild('createDialog') createDialog!: CreateInverterDialog;
  @ViewChild('inverterTpl', { static: true }) inverterTpl!: TemplateRef<any>;
  @ViewChild('sizeTpl', { static: true }) sizeTpl!: TemplateRef<any>;
  @ViewChild('priceTpl', { static: true }) priceTpl!: TemplateRef<any>;

  // State for edit mode
  editingInverter = signal<SolarInverter | null>(null);

  organizationId = computed(() => this.currentOrgStore.organizationId() || '');
  inverters = computed(() => this.equipmentStore.allInverters());
  isLoading = computed(() => this.equipmentStore.isLoading());

  columns: ColumnDef<SolarInverter>[] = [];

  ngOnInit() {
    this.columns = [
      {
        header: 'Wechselrichter',
        template: this.inverterTpl,
      },
      {
        header: 'Größe',
        template: this.sizeTpl,
        class: 'w-[120px] text-right',
      },
      {
        header: 'Preis',
        template: this.priceTpl,
        class: 'w-[140px] text-right',
      },
    ];
  }

  actions: TableAction<SolarInverter>[] = [
    {
      label: 'Bearbeiten',
      action: (item) => this.editInverter(item),
      visible: (item) => !item.isGlobal && this.currentOrgStore.can('equipment', 'update'),
    },
    {
      label: 'Löschen',
      type: 'destructive',
      action: (item) => this.deleteInverter(item),
      visible: (item) => !item.isGlobal && this.currentOrgStore.can('equipment', 'delete'),
      dividerBefore: true,
    },
  ];

  constructor() {
    // Legitimate side-effect use: triggers a store load (network I/O) whenever
    // the active organisation changes. This is not state derivation — it is an
    // imperative command to fetch data. No cleaner alternative exists here because
    // the load must re-fire reactively on orgId changes without an Observable pipeline.
    effect(() => {
      const orgId = this.organizationId();
      if (orgId) {
        this.equipmentStore.loadInverters(orgId);
      }
    });
  }

  async syncInverters() {
    try {
      const orgId = this.organizationId();
      if (orgId) {
        await this.equipmentStore.syncInverters(orgId);
      }
    } catch (error) {
      console.error('Error syncing inverters:', error);
    }
  }

  addInverter() {
    this.editingInverter.set(null);
    this.createDialog?.open();
  }

  editInverter(inverter: SolarInverter) {
    console.log('Edit inverter:', inverter);
    this.editingInverter.set(inverter);
    this.createDialog?.open();
  }

  async deleteInverter(inverter: SolarInverter) {
    if (
      confirm(
        `Möchten Sie den Wechselrichter "${inverter.name}" wirklich löschen?`
      )
    ) {
      try {
        const orgId = this.organizationId();
        if (orgId) {
          await this.equipmentStore.deleteInverter(orgId, inverter.id);
        }
      } catch (error) {
        console.error('Error deleting inverter:', error);
      }
    }
  }
}
